<script setup lang="ts">
import CompetenceRadarCard from '@/views/pages/reports/analytics/CompetenceRadarCard.vue'
import CompetenceTopPlayersCard from '@/views/pages/reports/analytics/CompetenceTopPlayersCard.vue'
import ProgressOverTimeCard from '@/views/pages/reports/analytics/ProgressOverTimeCard.vue'
import SkillGapHeatmapCard from '@/views/pages/reports/analytics/SkillGapHeatmapCard.vue'
import EmployeeComparisonCard from '@/views/pages/reports/analytics/EmployeeComparisonCard.vue'
import EmployeeReportCard from '@/views/pages/reports/analytics/EmployeeReportCard.vue'
import EngagementDashboardCard from '@/views/pages/reports/analytics/EngagementDashboardCard.vue'
import ScoreDistributionCard from '@/views/pages/reports/analytics/ScoreDistributionCard.vue'
import DepartmentAggregationCard from '@/views/pages/reports/analytics/DepartmentAggregationCard.vue'
import EvaluatorConsistencyCard from '@/views/pages/reports/analytics/EvaluatorConsistencyCard.vue'
import ReportFiltersBar from '@/views/pages/reports/ReportFiltersBar.vue'
import { useReportFilters } from '@/composables/useReportFilters'
import { hideAiResults } from '@/utils/helpers'
import { App, Modules } from '@/types/generated'
import UserData = App.Http.Controllers.Auth.UserData
import OrganizationSettingsData = Modules.Organization.OrganizationSettingsData

const { t } = useI18n()

const currentUserData = useCookie<UserData>('userData')
const orgSettings = useCookie<OrganizationSettingsData>('organizationSettings')
const hideAi = hideAiResults(currentUserData.value?.roles ?? [], orgSettings.value)

const reportFilters = useReportFilters({
  fields: ['period', 'competence', 'departments', 'positions', 'employees', 'scenarios'],
})

const { filters, isReady } = reportFilters

const gamesExportUrl = createUrl('/api/analytics/competence/games-players', { query: reportFilters.query })

definePage({
  meta: {
    action: 'read',
    subject: 'analytics',
  },
})
</script>

<template>
  <div>
    <VRow class="match-height">
      <VCol cols="12">
        <ReportFiltersBar :report-filters="reportFilters" />
      </VCol>
      <template v-if="isReady">
        <VCol cols="12">
          <CompetenceRadarCard :filters="filters" />
        </VCol>
        <VCol cols="12">
          <CompetenceTopPlayersCard :filters="filters" />
        </VCol>
        <VCol cols="12">
          <ProgressOverTimeCard :filters="filters" />
        </VCol>
        <VCol cols="12">
          <DepartmentAggregationCard :filters="filters" />
        </VCol>
        <VCol cols="12">
          <EvaluatorConsistencyCard :filters="filters" />
        </VCol>
        <VCol
          v-if="!hideAi"
          cols="12"
        >
          <EmployeeComparisonCard :filters="filters" />
        </VCol>
        <VCol cols="12">
          <ScoreDistributionCard :filters="filters" />
        </VCol>
        <VCol cols="12">
          <SkillGapHeatmapCard :filters="filters" />
        </VCol>
        <VCol cols="12">
          <EngagementDashboardCard :filters="filters" />
        </VCol>
        <VCol
          v-if="!hideAi"
          cols="12"
        >
          <EmployeeReportCard />
        </VCol>
        <VCol cols="12">
          <VCard>
            <VCardItem class="d-flex flex-wrap justify-space-between gap-4">
              <VCardTitle>{{ t(`statistics.player_competence_radar.by_games`) }}</VCardTitle>
              <template #append>
                <VBtn
                  variant="tonal"
                  density="comfortable"
                  color="secondary"
                  prepend-icon="tabler-upload"
                  :href="gamesExportUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Excel
                </VBtn>
              </template>
            </VCardItem>
          </VCard>
        </VCol>
      </template>
    </VRow>
  </div>
</template>

<style lang="scss">
@use "@core-scss/template/libs/apex-chart.scss";
</style>
